import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, Lock, LogOut, RefreshCw, Users } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { LogoMark } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sjk_admin_token";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [subscribers, setSubscribers] = useState(null);
    const [fetching, setFetching] = useState(false);

    const authed = (extra = {}) => ({ headers: { Authorization: `Bearer ${token}` }, ...extra });

    const load = useCallback(async () => {
        if (!token) return;
        setFetching(true);
        try {
            const { data } = await axios.get(`${API}/admin/subscribers`, authed());
            setSubscribers(data.subscribers);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                setSubscribers(null);
            } else {
                toast.error("Could not load the list", { description: "Please try again." });
            }
        } finally {
            setFetching(false);
        }
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const login = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            setToken(data.access_token);
            setPassword("");
            toast.success("Welcome back, Chef");
        } catch (err) {
            toast.error("Login failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setSubscribers(null);
    };

    const downloadCsv = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/subscribers.csv`, authed({ responseType: "blob" }));
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "spicejam-launch-list.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed", { description: "Please try again." });
        }
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-obsidian px-5" data-testid="admin-login-screen">
                <form
                    onSubmit={login}
                    className="w-full max-w-sm rounded-3xl border border-white/10 bg-soot p-8"
                    data-testid="admin-login-form"
                >
                    <div className="flex items-center gap-3">
                        <LogoMark className="h-10 w-10" />
                        <div>
                            <p className="font-display text-lg font-extrabold uppercase tracking-tight text-parchment">
                                SpiceJam <span className="text-nutmeg">Kitchen</span>
                            </p>
                            <p className="text-xs uppercase tracking-[0.25em] text-sand/60">Owner access</p>
                        </div>
                    </div>
                    <label className="mt-8 block text-xs font-semibold uppercase tracking-[0.2em] text-sand/70" htmlFor="admin-password">
                        Password
                    </label>
                    <Input
                        id="admin-password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="mt-2 h-12 border-white/15 bg-obsidian/60 text-parchment placeholder:text-sand/40 focus-visible:ring-nutmeg"
                        data-testid="admin-password-input"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-nutmeg px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-obsidian transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-nutmegwarm disabled:opacity-60"
                        data-testid="admin-login-button"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        {loading ? "Checking..." : "Sign in"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian px-5 py-10 sm:px-8" data-testid="admin-dashboard">
            <div className="mx-auto max-w-5xl">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <LogoMark className="h-10 w-10" />
                        <div>
                            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-parchment">
                                Launch list <span className="text-nutmeg">subscribers</span>
                            </h1>
                            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-sand/60">
                                <Users className="h-3.5 w-3.5" />
                                {subscribers ? `${subscribers.length} on the list` : "Loading..."}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={load}
                            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-sand transition-[border-color,color] duration-300 hover:border-nutmeg/60 hover:text-nutmeg"
                            data-testid="admin-refresh-button"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <button
                            onClick={downloadCsv}
                            className="flex items-center gap-2 rounded-full bg-nutmeg px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-obsidian transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-nutmegwarm"
                            data-testid="admin-download-csv-button"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download CSV
                        </button>
                        <button
                            onClick={logout}
                            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-sand transition-[border-color,color] duration-300 hover:border-bonnet/60 hover:text-bonnetglow"
                            data-testid="admin-logout-button"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Sign out
                        </button>
                    </div>
                </div>

                <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-soot" data-testid="admin-subscribers-table">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-[0.2em] text-sand/60">
                                <th className="px-6 py-4 font-semibold">Name</th>
                                <th className="px-6 py-4 font-semibold">Email</th>
                                <th className="hidden px-6 py-4 font-semibold md:table-cell">Message</th>
                                <th className="px-6 py-4 font-semibold">Signed up</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscribers && subscribers.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-sand/60" data-testid="admin-empty-state">
                                        No one on the list yet — share the site and watch it grow.
                                    </td>
                                </tr>
                            )}
                            {subscribers &&
                                subscribers.map((s) => (
                                    <tr key={s.id} className="border-b border-white/5 text-sand transition-colors duration-200 hover:bg-white/5" data-testid={`admin-subscriber-row-${s.id}`}>
                                        <td className="px-6 py-4 font-semibold text-parchment">{s.name}</td>
                                        <td className="px-6 py-4">{s.email}</td>
                                        <td className="hidden max-w-xs truncate px-6 py-4 text-sand/70 md:table-cell">{s.message || "—"}</td>
                                        <td className="whitespace-nowrap px-6 py-4 text-sand/70">
                                            {s.created_at ? new Date(s.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—"}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                <p className="mt-6 text-xs text-sand/50">
                    Every signup is also emailed to spicejamkitchen@gmail.com. This page is private — keep the link and password to yourself.
                </p>
            </div>
        </div>
    );
}
